/**
 * CreateContainerPage — organizer creates a sea/air consolidated-freight
 * container in `draft` status. Per-vehicle road modes and air-freight
 * are out-of-scope for the web form (use the Flutter app — they need
 * specialised picker UX).
 *
 * Server: POST /api/cargo/containers
 * Schema: createContainerSchema in middleware/validations/containerValidation.js
 *
 * Required fields enforced by Joi:
 *   title (3-200), originCountry, destinationCountry,
 *   joinsCloseAt, warehouseCutoffAt
 *   For sea/air (default sea_container): containerType, originPortCode,
 *     destinationPortCode (unless isTentative=true), pricingMode
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Container, Loader, AlertCircle, Info,
} from 'lucide-react';
import { useCreateContainer } from '@/hooks/useContainers';
import { useMyCompany } from '@/hooks/useCompany';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/common/TextField';
import { CONTAINER_COUNTRIES } from '@/constants/countries';
import type {
  CreateContainerInput, ContainerType, PricingMode, TransportMode,
} from '@/api/containers';

const FIELD_CLASSES =
  'w-full rounded-xl border border-border bg-surface px-4 py-3 text-text-primary outline-none transition focus:border-primary';

const TRANSPORT_MODES: { value: TransportMode; label: string; supported: boolean }[] = [
  { value: 'sea_container',          label: 'حاوية بحرية',         supported: true },
  { value: 'air_freight',            label: 'شحن جوي',              supported: true },
  { value: 'road_truck',             label: 'شاحنة برية',           supported: false },
  { value: 'road_reefer',            label: 'مبرّد',                 supported: false },
  { value: 'road_vehicle_carrier',   label: 'ناقلة سيارات',         supported: false },
];

const CONTAINER_TYPES: { value: ContainerType; label: string }[] = [
  { value: '20ft', label: '20ft' },
  { value: '40ft', label: '40ft' },
  { value: '40hc', label: '40HC' },
  { value: 'lcl',  label: 'LCL (less than container load)' },
  { value: 'air',  label: 'جوي' },
];

const PRICING_MODES: { value: PricingMode; label: string; hint: string }[] = [
  { value: 'per_kg',  label: 'بالوزن (per_kg)',   hint: 'سعر/كغم' },
  { value: 'per_cbm', label: 'بالحجم (per_cbm)',  hint: 'سعر/م³' },
  { value: 'mixed',   label: 'مختلط (mixed)',     hint: 'الأكبر بين الاثنين' },
];

const COMMON_CATEGORIES = [
  'cosmetics', 'electronics', 'fashion', 'home_furniture',
  'auto_parts', 'toys', 'sports', 'machinery', 'tools',
  'stationery', 'medical_devices',
];

const CATEGORY_LABELS: Record<string, string> = {
  cosmetics: 'مستحضرات تجميل',
  electronics: 'إلكترونيات',
  fashion: 'أزياء',
  home_furniture: 'منزلية + أثاث',
  auto_parts: 'قطع سيارات',
  toys: 'ألعاب',
  sports: 'رياضية',
  machinery: 'آلات',
  tools: 'أدوات',
  stationery: 'قرطاسية',
  medical_devices: 'أجهزة طبية',
};

interface FormState extends CreateContainerInput {
  // local-only fields
  _allowedCategoriesSet: Set<string>;
}

export default function CreateContainerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: company, isLoading: companyLoading } = useMyCompany();
  const create = useCreateContainer();

  const [form, setForm] = useState<FormState>({
    title: '',
    originCountry: 'cn',
    destinationCountry: 'sy',
    transportMode: 'sea_container',
    containerType: '40hc',
    pricingMode: 'per_kg',
    insuranceRateBps: 150,
    minimumChargeUsd: 0,
    clearanceFeeUsd: 0,
    requiresInvoice: true,
    requiresHsCode: false,
    isTentative: false,
    isRecurring: false,
    joinsCloseAt: '',
    warehouseCutoffAt: '',
    _allowedCategoriesSet: new Set<string>(),
  });
  const [error, setError] = useState<string | null>(null);

  if (companyLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16 text-text-tertiary">
          <Loader size={32} className="animate-spin" />
        </div>
      </AppLayout>
    );
  }

  // Server enforces this server-side; the client check just avoids one
  // wasted POST + gives a helpful message immediately.
  const canCreate = company
    && company.status === 'approved'
    && company.company_type === 'international'
    && (company.operator_mode === 'operator' || company.operator_mode === 'both');

  if (!canCreate) {
    return (
      <AppLayout>
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary">
          <ArrowRight size={18} /> {t('back', 'رجوع')}
        </button>
        <div className="max-w-lg mx-auto bg-surface border border-warning/40 rounded-2xl p-6 text-center">
          <AlertCircle size={48} className="mx-auto text-warning mb-3" />
          <h2 className="text-lg font-bold text-text-primary mb-2">
            {t('container_create_blocked', 'إنشاء الحاويات للشركات الدولية المعتمدة كمشغّل (operator)')}
          </h2>
          <p className="text-text-secondary text-sm">
            {!company
              ? t('container_create_no_company', 'سجّل شركتك أولاً.')
              : company.company_type !== 'international'
              ? t('container_create_local', 'هذه الميزة للشركات الدولية. شركتك المحلية تستطيع إنشاء رحلات بدلاً من الحاويات.')
              : company.status !== 'approved'
              ? t('container_create_not_approved', 'في انتظار اعتماد الإدارة لشركتك.')
              : t('container_create_shipper_only', 'وضع التشغيل الحالي "shipper" — للحاويات حوّل لـ operator أو both.')}
          </p>
        </div>
      </AppLayout>
    );
  }

  const isSeaAir = form.transportMode === 'sea_container' || form.transportMode === 'air_freight';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (form.title.trim().length < 3) return setError(t('error_title_short', 'العنوان قصير جداً (3 أحرف على الأقل)'));
    if (form.originCountry === form.destinationCountry) return setError(t('error_origin_dest_same', 'بلد المنشأ والوجهة يجب أن يكونا مختلفين'));
    if (!form.joinsCloseAt) return setError(t('error_joins_close_at', 'موعد إغلاق التسجيل إلزامي'));
    if (!form.warehouseCutoffAt) return setError(t('error_warehouse_cutoff_at', 'موعد إغلاق المستودع إلزامي'));

    if (isSeaAir) {
      if (!form.containerType) return setError(t('error_container_type', 'نوع الحاوية إلزامي'));
      if (!form.pricingMode) return setError(t('error_pricing_mode', 'نمط التسعير إلزامي'));

      // Ports required unless tentative
      if (!form.isTentative) {
        if (!form.originPortCode) return setError(t('error_origin_port', 'كود ميناء المنشأ إلزامي (إلا إذا كانت مبدئية)'));
        if (!form.destinationPortCode) return setError(t('error_dest_port', 'كود ميناء الوجهة إلزامي (إلا إذا كانت مبدئية)'));
      }

      // Pricing fields per mode
      if (form.pricingMode === 'per_kg' && !form.pricePerKgUsd) {
        return setError(t('error_price_per_kg', 'السعر بالوزن إلزامي عند نمط per_kg'));
      }
      if (form.pricingMode === 'per_cbm' && !form.pricePerCbmUsd) {
        return setError(t('error_price_per_cbm', 'السعر بالحجم إلزامي عند نمط per_cbm'));
      }
      if (form.pricingMode === 'mixed' && (!form.pricePerKgUsd || !form.pricePerCbmUsd || !form.pricingMixedRule)) {
        return setError(t('error_price_mixed', 'النمط المختلط يحتاج: السعر بالوزن + السعر بالحجم + قاعدة الاختيار'));
      }
    }

    try {
      // Strip the local-only Set wrapper before POST
      const { _allowedCategoriesSet, ...rest } = form;
      const payload: CreateContainerInput = {
        ...rest,
        allowedCategories: Array.from(_allowedCategoriesSet),
        // Uppercase port codes (server's Joi enforces uppercase)
        originPortCode: form.originPortCode?.trim().toUpperCase() || undefined,
        destinationPortCode: form.destinationPortCode?.trim().toUpperCase() || undefined,
      };
      const created = await create.mutateAsync(payload);
      navigate(`/containers/${created.id}`, { replace: true });
    } catch (e: any) {
      setError(e?.message || t('error_create_failed', 'فشل إنشاء الحاوية'));
    }
  };

  const toggleCategory = (cat: string) => {
    setForm((f) => {
      const next = new Set(f._allowedCategoriesSet);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return { ...f, _allowedCategoriesSet: next };
    });
  };

  return (
    <AppLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary"
      >
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="max-w-2xl mx-auto">
        <h1 className="text-xl font-bold text-primary mb-1 flex items-center gap-2">
          <Container size={20} /> {t('new_container', 'إنشاء حاوية')}
        </h1>
        <p className="text-text-secondary text-sm mb-5">
          {t('new_container_subtitle', 'الحاوية تبدأ كمسوّدة. تنشرها لاحقاً من صفحة التفاصيل لتظهر للمرسلين.')}
        </p>

        {error && (
          <div className="bg-error/10 text-error rounded-xl p-3 text-sm mb-4 flex items-center gap-2">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        <form onSubmit={submit} className="bg-surface border border-border rounded-2xl p-5 space-y-4">
          {/* Title */}
          <TextField
            label={t('title_label', 'عنوان الحاوية') + ' *'}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder={t('title_ph', 'مثال: حاوية الصين → اللاذقية أبريل')}
            required
            minLength={3}
            maxLength={200}
          />

          {/* Description */}
          <Field label={t('description', 'الوصف')}>
            <textarea
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className={FIELD_CLASSES + ' min-h-[60px]'}
              maxLength={2000}
              placeholder={t('desc_ph', 'تفاصيل الخط، السرعة المتوقعة، شركات النقل، إلخ.')}
            />
          </Field>

          {/* Tentative + Recurring */}
          <div className="grid grid-cols-2 gap-3">
            <Toggle
              label={t('is_tentative', 'مبدئية (قبل الحجز)')}
              hint={t('is_tentative_hint', 'تظهر للمرسلين كخطة. الموانئ يمكن تركها فارغة.')}
              checked={!!form.isTentative}
              onChange={(v) => setForm({ ...form, isTentative: v })}
            />
            <Toggle
              label={t('is_recurring', 'منتظمة')}
              hint={t('is_recurring_hint', 'هل تكرر هذا المسار بانتظام؟')}
              checked={!!form.isRecurring}
              onChange={(v) => setForm({ ...form, isRecurring: v })}
            />
          </div>
          {form.isRecurring && (
            <Field label={t('recurrence_freq', 'تواتر التكرار') + ' *'}>
              <select
                value={form.recurrenceFrequency || ''}
                onChange={(e) => setForm({ ...form, recurrenceFrequency: e.target.value as any })}
                className={FIELD_CLASSES}
                required
              >
                <option value="">—</option>
                <option value="weekly">أسبوعي</option>
                <option value="biweekly">كل أسبوعين</option>
                <option value="monthly">شهري</option>
                <option value="bimonthly">كل شهرين</option>
                <option value="quarterly">ربع سنوي</option>
              </select>
            </Field>
          )}

          {/* Transport mode + Container type */}
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('transport_mode', 'وسيلة النقل') + ' *'}>
              <select
                value={form.transportMode}
                onChange={(e) => setForm({ ...form, transportMode: e.target.value as TransportMode })}
                className={FIELD_CLASSES}
                required
              >
                {TRANSPORT_MODES.map((m) => (
                  <option key={m.value} value={m.value} disabled={!m.supported}>
                    {m.label}{!m.supported ? ' — استخدم الموبايل' : ''}
                  </option>
                ))}
              </select>
            </Field>
            {isSeaAir && (
              <Field label={t('container_type', 'نوع الحاوية') + ' *'}>
                <select
                  value={form.containerType}
                  onChange={(e) => setForm({ ...form, containerType: e.target.value as ContainerType })}
                  className={FIELD_CLASSES}
                  required
                >
                  {CONTAINER_TYPES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </Field>
            )}
          </div>

          {/* Route */}
          <fieldset className="space-y-3 pt-2 border-t border-border/50">
            <legend className="text-sm font-semibold text-text-secondary mb-2">
              {t('route', 'المسار')}
            </legend>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('origin_country', 'بلد المنشأ') + ' *'}>
                <select
                  value={form.originCountry}
                  onChange={(e) => setForm({ ...form, originCountry: e.target.value })}
                  className={FIELD_CLASSES} required
                >
                  {CONTAINER_COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </Field>
              <Field label={t('destination_country', 'بلد الوجهة') + ' *'}>
                <select
                  value={form.destinationCountry}
                  onChange={(e) => setForm({ ...form, destinationCountry: e.target.value })}
                  className={FIELD_CLASSES} required
                >
                  {CONTAINER_COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </Field>
            </div>
            {isSeaAir && (
              <div className="grid grid-cols-2 gap-3">
                <TextField
                  label={t('origin_port_code', 'كود ميناء المنشأ') + (form.isTentative ? '' : ' *')}
                  value={form.originPortCode || ''}
                  onChange={(e) => setForm({ ...form, originPortCode: e.target.value })}
                  dir="ltr"
                  maxLength={16}
                  placeholder={t('port_code_ph', 'CNSHA / TRIST / ...')}
                  required={!form.isTentative}
                />
                <TextField
                  label={t('dest_port_code', 'كود ميناء الوجهة') + (form.isTentative ? '' : ' *')}
                  value={form.destinationPortCode || ''}
                  onChange={(e) => setForm({ ...form, destinationPortCode: e.target.value })}
                  dir="ltr"
                  maxLength={16}
                  placeholder="SYLTK / SYTAR"
                  required={!form.isTentative}
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label={t('origin_port_name', 'اسم ميناء المنشأ')}
                value={form.originPortName || ''}
                onChange={(e) => setForm({ ...form, originPortName: e.target.value })}
              />
              <TextField
                label={t('dest_port_name', 'اسم ميناء الوجهة')}
                value={form.destinationPortName || ''}
                onChange={(e) => setForm({ ...form, destinationPortName: e.target.value })}
              />
            </div>
          </fieldset>

          {/* Capacity */}
          <fieldset className="space-y-3 pt-2 border-t border-border/50">
            <legend className="text-sm font-semibold text-text-secondary mb-2">
              {t('capacity', 'السعة')}
            </legend>
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label={t('max_weight_kg', 'الحد الأعلى للوزن (kg)')}
                type="number"
                value={form.maxWeightKg ?? ''}
                onChange={(e) => setForm({ ...form, maxWeightKg: e.target.value === '' ? undefined : Number(e.target.value) })}
                min={1} dir="ltr"
              />
              <TextField
                label={t('max_cbm', 'الحد الأعلى للحجم (m³)')}
                type="number"
                value={form.maxCbm ?? ''}
                onChange={(e) => setForm({ ...form, maxCbm: e.target.value === '' ? undefined : Number(e.target.value) })}
                min={0} step={0.01} dir="ltr"
              />
            </div>
          </fieldset>

          {/* Pricing */}
          <fieldset className="space-y-3 pt-2 border-t border-border/50">
            <legend className="text-sm font-semibold text-text-secondary mb-2">
              {t('pricing', 'التسعير')}
            </legend>
            <Field label={t('pricing_mode_label', 'نمط التسعير') + ' *'}>
              <select
                value={form.pricingMode}
                onChange={(e) => setForm({ ...form, pricingMode: e.target.value as PricingMode })}
                className={FIELD_CLASSES} required
              >
                {PRICING_MODES.map((m) => (
                  <option key={m.value} value={m.value}>{m.label} — {m.hint}</option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              {(form.pricingMode === 'per_kg' || form.pricingMode === 'mixed') && (
                <TextField
                  label={t('price_per_kg_usd', 'السعر / kg (USD)') + ' *'}
                  type="number"
                  value={form.pricePerKgUsd ?? ''}
                  onChange={(e) => setForm({ ...form, pricePerKgUsd: e.target.value === '' ? undefined : Number(e.target.value) })}
                  min={0} step={0.01} required dir="ltr"
                />
              )}
              {(form.pricingMode === 'per_cbm' || form.pricingMode === 'mixed') && (
                <TextField
                  label={t('price_per_cbm_usd', 'السعر / m³ (USD)') + ' *'}
                  type="number"
                  value={form.pricePerCbmUsd ?? ''}
                  onChange={(e) => setForm({ ...form, pricePerCbmUsd: e.target.value === '' ? undefined : Number(e.target.value) })}
                  min={0} step={0.01} required dir="ltr"
                />
              )}
            </div>
            {form.pricingMode === 'mixed' && (
              <Field label={t('mixed_rule', 'قاعدة المختلط') + ' *'}>
                <select
                  value={form.pricingMixedRule || ''}
                  onChange={(e) => setForm({ ...form, pricingMixedRule: e.target.value as 'max' | 'sum' })}
                  className={FIELD_CLASSES} required
                >
                  <option value="">—</option>
                  <option value="max">الأكبر بين الاثنين (max)</option>
                  <option value="sum">المجموع (sum)</option>
                </select>
              </Field>
            )}
            <div className="grid grid-cols-3 gap-3">
              <TextField
                label={t('min_charge_usd', 'الحد الأدنى للسعر (USD)')}
                type="number"
                value={form.minimumChargeUsd ?? 0}
                onChange={(e) => setForm({ ...form, minimumChargeUsd: Number(e.target.value) })}
                min={0} dir="ltr"
              />
              <TextField
                label={t('clearance_fee_usd', 'رسوم التخليص (USD)')}
                type="number"
                value={form.clearanceFeeUsd ?? 0}
                onChange={(e) => setForm({ ...form, clearanceFeeUsd: Number(e.target.value) })}
                min={0} dir="ltr"
              />
              <TextField
                label={t('insurance_rate_bps', 'نسبة التأمين (bps)')}
                type="number"
                value={form.insuranceRateBps ?? 150}
                onChange={(e) => setForm({ ...form, insuranceRateBps: Number(e.target.value) })}
                min={0} max={10000} dir="ltr"
              />
            </div>
            <p className="text-xs text-text-tertiary -mt-2 flex items-center gap-1">
              <Info size={11} /> {t('bps_hint', 'bps = 1/100 من 1%. مثلاً 150 = 1.5% من القيمة المُصرَّحة.')}
            </p>
          </fieldset>

          {/* Schedule */}
          <fieldset className="space-y-3 pt-2 border-t border-border/50">
            <legend className="text-sm font-semibold text-text-secondary mb-2">
              {t('schedule', 'الجدولة')}
            </legend>
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label={t('joins_close_at', 'إغلاق التسجيل') + ' *'}
                type="datetime-local"
                value={form.joinsCloseAt}
                onChange={(e) => setForm({ ...form, joinsCloseAt: e.target.value })}
                required dir="ltr"
              />
              <TextField
                label={t('warehouse_cutoff_at', 'إغلاق المستودع') + ' *'}
                type="datetime-local"
                value={form.warehouseCutoffAt}
                onChange={(e) => setForm({ ...form, warehouseCutoffAt: e.target.value })}
                required dir="ltr"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label={t('estimated_departure_at', 'الإقلاع التقديري')}
                type="date"
                value={form.estimatedDepartureAt || ''}
                onChange={(e) => setForm({ ...form, estimatedDepartureAt: e.target.value })}
                dir="ltr"
              />
              <TextField
                label={t('estimated_arrival_at', 'الوصول التقديري')}
                type="date"
                value={form.estimatedArrivalAt || ''}
                onChange={(e) => setForm({ ...form, estimatedArrivalAt: e.target.value })}
                dir="ltr"
              />
            </div>
          </fieldset>

          {/* Categories */}
          <fieldset className="space-y-3 pt-2 border-t border-border/50">
            <legend className="text-sm font-semibold text-text-secondary mb-2">
              {t('allowed_categories', 'الفئات المسموحة')} ({form._allowedCategoriesSet.size})
            </legend>
            <div className="flex flex-wrap gap-2">
              {COMMON_CATEGORIES.map((cat) => {
                const active = form._allowedCategoriesSet.has(cat);
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => toggleCategory(cat)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition ${
                      active ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-surface text-text-secondary hover:border-primary/40'
                    }`}
                  >
                    {CATEGORY_LABELS[cat] || cat}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-text-tertiary">
              {t('categories_hint', 'فارغ = كل الفئات المسموحة قانونياً (الفئات الممنوعة محسومة على مستوى السيرفر).')}
            </p>
          </fieldset>

          {/* Constraints */}
          <fieldset className="space-y-3 pt-2 border-t border-border/50">
            <legend className="text-sm font-semibold text-text-secondary mb-2">
              {t('constraints', 'القيود')}
            </legend>
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label={t('max_piece_weight_kg', 'الحد الأعلى لوزن القطعة (kg)')}
                type="number"
                value={form.maxPieceWeightKg ?? ''}
                onChange={(e) => setForm({ ...form, maxPieceWeightKg: e.target.value === '' ? undefined : Number(e.target.value) })}
                min={0} step={0.1} dir="ltr"
              />
              <TextField
                label={t('max_declared_value_usd', 'الحد الأعلى للقيمة المُصرَّحة (USD)')}
                type="number"
                value={form.maxDeclaredValueUsd ?? ''}
                onChange={(e) => setForm({ ...form, maxDeclaredValueUsd: e.target.value === '' ? undefined : Number(e.target.value) })}
                min={0} dir="ltr"
              />
            </div>
            <div className="space-y-2">
              <Toggle
                label={t('requires_invoice', 'فاتورة المورّد إلزامية')}
                checked={!!form.requiresInvoice}
                onChange={(v) => setForm({ ...form, requiresInvoice: v })}
              />
              <Toggle
                label={t('requires_hs_code', 'كود HS إلزامي')}
                checked={!!form.requiresHsCode}
                onChange={(v) => setForm({ ...form, requiresHsCode: v })}
              />
            </div>
          </fieldset>

          {/* Submit */}
          <div className="flex gap-2 pt-2">
            <Button variant="primary" loading={create.isPending} type="submit">
              {t('create_as_draft', 'إنشاء كمسوّدة')}
            </Button>
            <Button variant="ghost" type="button" onClick={() => navigate(-1)}>
              {t('cancel', 'إلغاء')}
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-text-secondary mb-1 block">{label}</span>
      {children}
    </label>
  );
}

function Toggle({
  label, hint, checked, onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-3 cursor-pointer p-2 rounded-xl hover:bg-border/20 transition">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-primary"
      />
      <span className="flex-1">
        <span className="font-semibold text-sm text-text-primary block">{label}</span>
        {hint && <span className="text-xs text-text-tertiary">{hint}</span>}
      </span>
    </label>
  );
}
